/**
 * Вход в ЛК — копия legacy main_login.tpl (разметка 1:1).
 *
 * ЕДИНЫЙ ВХОД: одна форма и на жителя, и на админа. Шлём пару на /api/auth/token; бекенд
 * сам решает — участок это или админ, и в ответе ставит is_admin. Если админ, ответ несёт
 * АДМИНСКУЮ пару токенов (отдельные сессии), мы сохраняем её как админскую и уходим на
 * /admin. Так же в legacy общая форма входа с логином/паролем админа уводила в админку.
 * У жителя и админа сессии разные (rw_user_* / rw_admin_*) — не пересекаются.
 */

import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { api, ApiError } from '@/api/client'
import { RecallPassword } from '@/components/RecallPassword'
import { saveTokens, type TokenPair } from '@/api/tokens'
import { clearCache } from '@/lib/apiCache'
import type { Area } from '@/api/types'
import { useSession } from '@/session/SessionProvider'

interface LoginResult extends TokenPair {
  is_admin: boolean
  area?: Area
}

export function LoginPage() {
  const { user, refresh } = useSession()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (user !== null) return <Navigate to="/lk" replace />

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const login = String(form.get('login') ?? '')
    const password = String(form.get('password') ?? '')

    setBusy(true)
    setError(null)
    try {
      // Во вкладке мог остаться кеш прошлого пользователя — входит другой человек.
      clearCache()

      // Единый вход: бекенд сам разбирает — участок или админ (is_admin в ответе).
      const result = await api.post<LoginResult>('/api/auth/token', { login, password })
      if (result.is_admin) {
        saveTokens('admin', result)
        await refresh()
        navigate('/admin')
        return
      }

      saveTokens('user', result)
      await refresh()
      navigate('/lk')
    } catch (e: unknown) {
      // legacy: $data.autherror — красный текст над формой
      setError(e instanceof ApiError ? e.message : 'Неверный логин или пароль')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="row">
      <div className="col-md-4 col-md-offset-4" style={{ paddingTop: 20 }}>
        {error !== null && <div style={{ color: 'red' }}>{error}</div>}
        <form method="post" onSubmit={(e) => void submit(e)}>
          <table className="table">
            <tbody>
              <tr>
                <td>Логин</td>
                <td>
                  <input type="text" name="login" className="form-control" required />
                </td>
              </tr>
              <tr>
                <td>Пароль</td>
                <td>
                  <input type="password" name="password" className="form-control" required />
                </td>
              </tr>
              <tr>
                <td />
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="submit"
                    name="auth"
                    value={busy ? 'Проверка…' : 'Вход'}
                    className="btn btn-info form-control"
                    disabled={busy}
                  />
                  {/* Ссылка «Вспомнить пароль» — в той же ячейке, что кнопка «Вход»,
                      поэтому центрируется прямо под ней. */}
                  <RecallPassword mode="login" />
                </td>
              </tr>
            </tbody>
          </table>
        </form>
      </div>
    </div>
  )
}
